/* PRODUCTION STATUS — SEWING (Hieu's feedback, decided by Jay 02–03/Nov/2026; Kayla's Sewing plan view 03/11). The pieces
   70_ProductionStatus.jsx uses:
   - SewingEditor: inside the lot window — one row per sewing line (part, line #, workers, plan / actual start and end, late start
     reason) and the week grid of daily output. The order's Skin / Case sewing steps are derived from these lines on the server
     (earliest start, latest end, actual end once every line has finished), so nobody types the same date twice.
   - SewingPlan: the tab next to Orders — one row per sewing line across orders (By line), or one per order with Skin and Case
     side by side (By order, Hieu's layout), so PT checks vendors' sewing plans without opening each style.
   - LineListDialog / LineRequestDialog: each vendor's line list (In-house lines and Subcons). A vendor requests a new entry and may
     use it at once ("pending approval"); HEVN PT approves or rejects. A rejected entry cannot be saved on an order.
   Rules decide here, never AI: lateness, warnings and the late start reason are the same for the same input. */
import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { X, Plus, Trash2, Check } from 'lucide-react';
import { SortTh } from './15_SampleTracker';
import useColumnTools from './useColumnTools';
import { BACKEND_URL } from './backendConfig';

const API = `${BACKEND_URL}/api/psr`;
const errOf = (e) => e?.response?.data?.error || e.message;
export const PARTS = ['skin', 'case', 'full'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dm = (iso) => (iso ? `${iso.slice(8, 10)}/${MON[Number(iso.slice(5, 7)) - 1]}` : '');
const dmy = (iso) => (iso ? `${dm(iso)}/${iso.slice(0, 4)}` : '');
const addDays = (iso, n) => { const d = new Date(`${iso}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const mondayOf = (iso) => addDays(iso, -((new Date(`${iso}T00:00:00Z`).getUTCDay() + 6) % 7));
const num = (n) => Number(n || 0).toLocaleString('en-US');
/* late start: actual start after plan start, or plan start passed with nothing started (spec) */
export const lateStart = (x, today) => !!x.planStart && (x.actStart ? x.actStart > x.planStart : x.planStart < today);
const outOf = (r) => (r.carried || 0) + Object.values(r.output || {}).reduce((a, b) => a + (Number(b) || 0), 0);

export function LineName({ entry }) {
  const { t } = useTranslation();
  if (!entry) return <span className="psr-muted">{t('xPsr.noLineNo')}</span>;
  return (
    <span className={`psr-ln ${entry.status}`}>
      {t(`xPsr.kind_${entry.kind}`)} · {entry.name}
      {entry.status === 'pending' && <em> ({t('xPsr.linePending')})</em>}
      {entry.status === 'rejected' && <em> ({t('xPsr.lineRejected')})</em>}
    </span>
  );
}
const lineText = (t, x) => (x && x.lineName ? `${t(`xPsr.kind_${x.lineKind}`)} · ${x.lineName}` : '');

/* ---------- the lot window's sewing lines and daily output ---------- */
export function SewingEditor({ rows, setRows, lineList, canEdit, today, reasons, onAskLine, isPt, editedOf }) {
  const { t } = useTranslation();
  const [week, setWeek] = useState(() => mondayOf(today));
  const live = rows.filter((r) => !r.removed);
  const entryOf = (r) => lineList.find((l) => l.id === Number(r.lineId)) || null;
  const nameOf = (r) => { const e = entryOf(r); return `${t(`xPsr.part_${r.part}`)} · ${e ? e.name : t('xPsr.noLineNo')}`; };
  const set = (id, f, v) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [f]: v === '' ? null : v } : r)));
  const setOut = (id, day, v) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, output: { ...r.output, [day]: v === '' ? null : Number(v) } } : r)));
  const add = () => setRows((rs) => [...rs, { id: `new-${Date.now()}`, part: rs.filter((r) => !r.removed).at(-1)?.part || 'skin', lineId: null, workers: null,
    planStart: null, planEnd: null, actStart: null, actEnd: null, lateReason: null, lateNote: null, output: {}, carried: 0 }]);
  const remove = (id) => setRows((rs) => (String(id).startsWith('new') ? rs.filter((r) => r.id !== id) : rs.map((r) => (r.id === id ? { ...r, removed: true } : r))));
  const days = Array.from({ length: 7 }, (_, i) => addDays(week, i));
  const dayNames = days.map((d) => new Intl.DateTimeFormat('en', { weekday: 'short', timeZone: 'UTC' }).format(new Date(`${d}T00:00:00Z`)));

  // warnings: told before saving, never blocking (spec: "Everything else is a warning")
  const warn = [];
  live.forEach((r) => { if (lateStart(r, today) && !r.lateReason) warn.push(t('xPsr.wLateReason', { line: nameOf(r) })); });
  PARTS.forEach((p) => {
    const L = live.filter((r) => r.part === p);
    if (L.some((r) => !r.lineId && (r.planStart || r.actStart))) warn.push(t('xPsr.wNoLine', { part: t(`xPsr.part_${p}`) }));
    const ids = L.map((r) => r.lineId).filter(Boolean).map(Number);
    if (new Set(ids).size < ids.length) warn.push(t('xPsr.wDupLine', { part: t(`xPsr.part_${p}`) }));
  });
  live.forEach((r) => {
    const ds = Object.entries(r.output || {}).filter(([, q]) => q != null && q !== '').map(([d]) => d).sort();
    if (!ds.length) return;
    if (!r.actStart) warn.push(t('xPsr.wOutNoStart', { line: nameOf(r) }));
    else if (ds[0] < r.actStart) warn.push(t('xPsr.wOutBefore', { line: nameOf(r), d: dmy(ds[0]) }));
  });
  const mark = (key) => {
    const l = editedOf(key);
    return l.length ? <span className="psr-edtag" title={`${t('xPsr.editedTip')}\n${l.join('\n')}`}>{t('xPsr.edited')}</span> : null;
  };
  const dateIn = (r, f) => {
    const act = f === 'actStart' || f === 'actEnd';
    return (
      <td className="psr-nowrap">
        <input type="date" value={r[f] || ''} max={act ? today : undefined} disabled={!canEdit} onChange={(e) => set(r.id, f, e.target.value)}
          aria-label={t(`xPsr.sew_${f}`)} data-psr-sew={`${r.id}:${f}`} />
        {act && canEdit && !r[f] && <button type="button" className="sp-btn psr-today" onClick={() => set(r.id, f, today)}>{t('xPsr.today')}</button>}
        {act && typeof r.id === 'number' && mark(`sew:${r.id}.${f}`)}
      </td>
    );
  };

  return (
    <section className="psr-sew" data-psr-sewing>
      <h4>{t('xPsr.sewTitle')}</h4>
      <div className="psr-steps-wrap">
        <table className="sp-table psr-sewtable">
          <thead><tr>
            <th>{t('xPsr.sew_part')}</th><th>{t('xPsr.sew_lineId')}</th><th>{t('xPsr.sew_workers')}</th><th>{t('xPsr.sew_planStart')}</th><th>{t('xPsr.sew_planEnd')}</th>
            <th>{t('xPsr.sew_actStart')}</th><th>{t('xPsr.sew_actEnd')}</th><th>{t('xPsr.sew_lateReason')}</th><th aria-label={t('xPsr.sewRemove')} />
          </tr></thead>
          <tbody>
            {!live.length && <tr><td colSpan={9} className="psr-muted">{t('xPsr.sewNone')}</td></tr>}
            {live.map((r) => {
              const cur = entryOf(r);
              const opts = lineList.filter((l) => l.status !== 'rejected' || l.id === Number(r.lineId));
              const late = lateStart(r, today);
              return (
                <tr key={r.id} data-psr-sewrow={r.id}>
                  <td>
                    <select value={r.part} disabled={!canEdit} onChange={(e) => set(r.id, 'part', e.target.value)} aria-label={t('xPsr.sew_part')} data-psr-sew={`${r.id}:part`}>
                      {PARTS.map((p) => <option key={p} value={p}>{t(`xPsr.part_${p}`)}</option>)}
                    </select>
                  </td>
                  <td>
                    <select value={r.lineId ?? ''} disabled={!canEdit} onChange={(e) => set(r.id, 'lineId', e.target.value ? Number(e.target.value) : null)}
                      aria-label={t('xPsr.sew_lineId')} className={cur?.status === 'rejected' ? 'psr-bad-in' : ''} data-psr-sew={`${r.id}:lineId`}>
                      <option value="">—</option>
                      {opts.map((l) => <option key={l.id} value={l.id}>{t(`xPsr.kind_${l.kind}`)} · {l.name}{l.status === 'pending' ? ` (${t('xPsr.linePending')})` : l.status === 'rejected' ? ` (${t('xPsr.lineRejected')})` : ''}</option>)}
                    </select>
                  </td>
                  <td><input type="number" min="0" step="1" className="psr-workers" value={r.workers ?? ''} disabled={!canEdit}
                    onChange={(e) => set(r.id, 'workers', e.target.value === '' ? null : Number(e.target.value))} aria-label={t('xPsr.sew_workers')} data-psr-sew={`${r.id}:workers`} /></td>
                  {dateIn(r, 'planStart')}{dateIn(r, 'planEnd')}{dateIn(r, 'actStart')}{dateIn(r, 'actEnd')}
                  <td>
                    {late || r.lateReason ? (
                      <div className="psr-latecell">
                        <select value={r.lateReason || ''} disabled={!canEdit} className={!r.lateReason ? 'psr-bad-in' : ''} onChange={(e) => set(r.id, 'lateReason', e.target.value)}
                          aria-label={t('xPsr.sew_lateReason')} data-psr-sew={`${r.id}:lateReason`}>
                          <option value="">{t('xPsr.lrPick')}</option>
                          {reasons.map((k) => <option key={k} value={k}>{t(`xPsr.lr_${k}`)}</option>)}
                        </select>
                        <input value={r.lateNote || ''} disabled={!canEdit} maxLength={300} placeholder={t('xPsr.sew_lateNote')} onChange={(e) => set(r.id, 'lateNote', e.target.value)}
                          aria-label={t('xPsr.sew_lateNote')} data-psr-sew={`${r.id}:lateNote`} />
                      </div>
                    ) : <span className="psr-muted">—</span>}
                  </td>
                  <td>{canEdit && <button type="button" className="sp-btn psr-icon-btn" onClick={() => remove(r.id)} title={t('xPsr.sewRemove')} aria-label={t('xPsr.sewRemove')} data-psr-sewdel={r.id}><Trash2 size={13} /></button>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {canEdit && (
        <div className="psr-inline psr-sew-actions">
          <button type="button" className="sp-btn" onClick={add} data-psr-sewadd><Plus size={13} /> {t('xPsr.sewAdd')}</button>
          <button type="button" className="sp-btn" onClick={onAskLine} data-psr-linereq>{isPt ? t('xPsr.sewReqPt') : t('xPsr.sewReq')}</button>
        </div>
      )}
      <p className="psr-muted psr-small">{t('xPsr.sewHelp')}</p>

      <h4>{t('xPsr.outTitle')}</h4>
      {!live.length ? <p className="psr-muted psr-small">{t('xPsr.outNeedLine')}</p> : (
        <>
          <div className="psr-steps-wrap">
            <table className="sp-table psr-outgrid" data-psr-outgrid>
              <thead><tr><th>{t('xPsr.sew_lineId')}</th>{days.map((d, i) => <th key={d} className={d === today ? 'today' : ''}>{dayNames[i]} {dm(d)}</th>)}<th>{t('xPsr.outWeek')}</th><th>{t('xPsr.outAll')}</th></tr></thead>
              <tbody>
                {live.map((r) => (
                  <tr key={r.id}>
                    <td className="psr-nowrap">{nameOf(r)}</td>
                    {days.map((d) => {
                      const l = typeof r.id === 'number' ? editedOf(`out:${r.id}.${d}`) : [];
                      return (
                        <td key={d}>
                          <input type="number" min="0" step="1" value={r.output?.[d] ?? ''} disabled={!canEdit || d > today} className={l.length ? 'psr-edited' : ''}
                            title={l.length ? `${t('xPsr.editedTip')}\n${l.join('\n')}` : undefined} onChange={(e) => setOut(r.id, d, e.target.value)}
                            aria-label={`${nameOf(r)} ${dmy(d)}`} data-psr-out={`${r.id}:${d}`} />
                        </td>
                      );
                    })}
                    <td className="num">{num(days.reduce((a, d) => a + (Number(r.output?.[d]) || 0), 0))}</td>
                    <td className="num"><b>{num(outOf(r))}</b></td>
                  </tr>
                ))}
                <tr className="psr-muted"><td>{t('xPsr.outDayTotal')}</td>{days.map((d) => { const s = live.reduce((a, r) => a + (Number(r.output?.[d]) || 0), 0); return <td key={d} className="num">{s ? num(s) : ''}</td>; })}<td /><td /></tr>
              </tbody>
            </table>
          </div>
          <div className="psr-inline psr-sew-actions">
            <button type="button" className="sp-btn" onClick={() => setWeek(addDays(week, -7))} data-psr-outprev>{t('xPsr.outPrev')}</button>
            <button type="button" className="sp-btn" onClick={() => setWeek(mondayOf(today))}>{t('xPsr.outThis')}</button>
            <button type="button" className="sp-btn" onClick={() => setWeek(addDays(week, 7))}>{t('xPsr.outNext')}</button>
            <span className="psr-muted psr-small">{t('xPsr.outHelp')}</span>
          </div>
          {live.some((r) => r.carried) && <p className="psr-muted psr-small">{t('xPsr.outCarried', { v: PARTS.map((p) => [p, live.filter((r) => r.part === p).reduce((a, r) => a + (r.carried || 0), 0)]).filter(([, n]) => n).map(([p, n]) => `${t(`xPsr.part_${p}`)} ${num(n)}`).join(' · ') })}</p>}
        </>
      )}
      {warn.length > 0 && (
        <div className="psr-warnbox" role="status" data-psr-sewwarn>
          <b>{t('xPsr.warnTitle')}</b>
          <ul>{[...new Set(warn)].map((w) => <li key={w}>{w}</li>)}</ul>
        </div>
      )}
    </section>
  );
}

/* what the lot window sends: only lines and days that changed, a new line whole, a removed line as { id, remove } */
export function sewingChanges(rows, base) {
  const was = new Map(base.map((x) => [x.id, x]));
  const F = ['part', 'lineId', 'workers', 'planStart', 'planEnd', 'actStart', 'actEnd', 'lateReason', 'lateNote'];
  const out = [];
  rows.forEach((r) => {
    const o = was.get(r.id);
    if (!o) { if (!r.removed) out.push({ id: r.id, ...Object.fromEntries(F.map((f) => [f, r[f] ?? null])), output: r.output }); return; }
    if (r.removed) { out.push({ id: r.id, remove: true }); return; }
    const ch = { id: r.id };
    F.forEach((f) => { if (String(r[f] ?? '') !== String(o[f] ?? '')) ch[f] = r[f] ?? null; });
    const days = {};
    new Set([...Object.keys(r.output || {}), ...Object.keys(o.output || {})]).forEach((d) => {
      const a = r.output?.[d] ?? null; const b = o.output?.[d] ?? null;
      if (a !== b) days[d] = a;
    });
    if (Object.keys(days).length) ch.output = days;
    if (Object.keys(ch).length > 1) out.push(ch);
  });
  return out;
}

/* ---------- the vendor's line list ---------- */
export function LineRequestDialog({ me, vendors, vendor, onClose, onDone }) {
  const { t } = useTranslation();
  const [f, setF] = useState({ vendor: vendor || (me.isVendor ? '' : vendors[0]?.key || ''), kind: 'inhouse', name: '', company: '', address: '', sews: '' });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const send = async () => {
    setBusy(true); setErr(null);
    try { const r = await axios.post(`${API}/line-list`, f); onDone(r.data); } catch (e) { setErr(errOf(e)); } finally { setBusy(false); }
  };
  return ReactDOM.createPortal(
    <div className="psr-back psr-top" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="psr-dialog psr-small-dlg" role="dialog" aria-modal="true" aria-labelledby="psr-lrq-title" data-psr-lrq>
        <div className="psr-dlg-head"><div><h3 id="psr-lrq-title">{me.pt ? t('xPsr.llAdd') : t('xPsr.llRequest')}</h3>
          <div className="psr-muted psr-small">{me.pt ? t('xPsr.lrqHelpPt', { v: f.vendor || '—' }) : t('xPsr.lrqHelpVendor')}</div></div>
          <button type="button" className="psr-x" onClick={onClose} aria-label={t('xPsr.close')}><X size={18} /></button></div>
        <div className="psr-dlg-body">
          {err && <p className="sp-error" role="alert">{err}</p>}
          <section className="psr-grid2">
            {!me.isVendor && !vendor && (
              <label className="psr-fld"><span>{t('xPsr.vendor')}</span>
                <select value={f.vendor} onChange={(e) => set('vendor', e.target.value)} data-psr-lrq-f="vendor">{vendors.map((v) => <option key={v.key} value={v.key}>{v.key} · {v.name}</option>)}</select>
              </label>
            )}
            <label className="psr-fld"><span>{t('xPsr.llType')}</span>
              <select value={f.kind} onChange={(e) => set('kind', e.target.value)} data-psr-lrq-f="kind">
                <option value="inhouse">{t('xPsr.kind_inhouse')}</option><option value="subcon">{t('xPsr.kind_subcon')}</option>
              </select>
            </label>
            <label className="psr-fld"><span>{t('xPsr.llName')}</span>
              <input value={f.name} maxLength={100} placeholder={t('xPsr.lrqNamePh')} onChange={(e) => set('name', e.target.value)} data-psr-lrq-f="name" />
            </label>
            {f.kind === 'subcon' && (
              <>
                <label className="psr-fld"><span>{t('xPsr.lrqCompany')}</span><input value={f.company} maxLength={200} onChange={(e) => set('company', e.target.value)} data-psr-lrq-f="company" /></label>
                <label className="psr-fld psr-wide"><span>{t('xPsr.lrqAddress')}</span><input value={f.address} maxLength={300} onChange={(e) => set('address', e.target.value)} data-psr-lrq-f="address" /></label>
                <label className="psr-fld psr-wide"><span>{t('xPsr.lrqSews')}</span><input value={f.sews} maxLength={200} onChange={(e) => set('sews', e.target.value)} data-psr-lrq-f="sews" /></label>
              </>
            )}
          </section>
          {f.kind === 'subcon' && <p className="psr-muted psr-small">{t('xPsr.lrqNoAccount')}</p>}
        </div>
        <div className="psr-dlg-foot">
          <button type="button" className="sp-btn" onClick={onClose}>{t('xPsr.close')}</button>
          <button type="button" className="sp-btn primary" disabled={busy || !f.name.trim()} onClick={send} data-psr-lrq-send>{me.pt ? t('xPsr.lrqAddBtn') : t('xPsr.lrqSend')}</button>
        </div>
      </div>
    </div>, document.body,
  );
}

export function LineListDialog({ me, vendors, vendor, onClose, onChanged }) {
  const { t } = useTranslation();
  const [list, setList] = useState(null);
  const [err, setErr] = useState(null);
  const [asking, setAsking] = useState(false);
  const [onlyPending, setOnlyPending] = useState(!!me.pt);
  const load = () => axios.get(`${API}/line-list`, { params: { vendor: vendor || undefined } }).then((r) => setList(r.data.lines)).catch((e) => setErr(errOf(e)));
  useEffect(() => { load(); }, [vendor]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const esc = (e) => { if (e.key === 'Escape' && !asking) onClose(); }; document.addEventListener('keydown', esc); return () => document.removeEventListener('keydown', esc); }, [onClose, asking]);
  const decide = async (l, decision) => {
    try { await axios.post(`${API}/line-list/${l.id}/decide`, { decision }); await load(); onChanged(); } catch (e) { setErr(errOf(e)); }
  };
  const pending = (list || []).filter((l) => l.status === 'pending').length;
  const shown = (list || []).filter((l) => !onlyPending || l.status === 'pending');
  return ReactDOM.createPortal(
    <div className="psr-back" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="psr-dialog" role="dialog" aria-modal="true" aria-labelledby="psr-ll-title" data-psr-linelist>
        <div className="psr-dlg-head"><div><h3 id="psr-ll-title">{t('xPsr.llTitle')}</h3><div className="psr-muted psr-small">{t('xPsr.llHelp')}</div></div>
          <button type="button" className="psr-x" onClick={onClose} aria-label={t('xPsr.close')}><X size={18} /></button></div>
        <div className="psr-dlg-body">
          {err && <p className="sp-error" role="alert">{err}</p>}
          <div className="psr-inline">
            {me.pt && <label className="psr-check"><input type="checkbox" checked={onlyPending} onChange={(e) => setOnlyPending(e.target.checked)} data-psr-llpending /> {t('xPsr.llOnlyPending', { n: pending })}</label>}
            <span className="mh-grow" />
            {me.canEdit && <button type="button" className="sp-btn" onClick={() => setAsking(true)} data-psr-lladd><Plus size={13} /> {me.pt ? t('xPsr.llAdd') : t('xPsr.llRequest')}</button>}
          </div>
          {!list ? <p className="psr-muted">{t('xPsr.loading')}</p> : !shown.length ? <p className="psr-muted">{onlyPending ? t('xPsr.llNoPending') : t('xPsr.llEmpty')}</p> : (
            <table className="sp-table psr-lltable">
              <thead><tr>{!me.isVendor && <th>{t('xPsr.colVendor')}</th>}<th>{t('xPsr.llType')}</th><th>{t('xPsr.llName')}</th><th>{t('xPsr.llDetails')}</th>
                <th>{t('xPsr.llStatus')}</th><th className="num">{t('xPsr.llUsed')}</th><th>{t('xPsr.llRequested')}</th>{me.pt && <th>{t('xPsr.colAction')}</th>}</tr></thead>
              <tbody>
                {shown.map((l) => (
                  <tr key={l.id} data-psr-llrow={l.id}>
                    {!me.isVendor && <td>{l.vendor}</td>}
                    <td>{t(`xPsr.kind_${l.kind}`)}</td>
                    <td><b>{l.name}</b></td>
                    <td className="psr-small">{l.kind === 'subcon' ? [l.company, l.address, l.sews].filter(Boolean).join(' · ') : '—'}</td>
                    <td><span className={`psr-ls ${l.status}`}>{t(`xPsr.ls_${l.status}`)}</span>{l.decideNote ? <div className="psr-muted psr-small">{l.decideNote}</div> : null}</td>
                    <td className="num">{l.used || 0}</td>
                    <td className="psr-small psr-nowrap">{l.requestedBy || '—'}<div className="psr-muted">{l.createdAt ? dmy(String(l.createdAt).slice(0, 10)) : ''}</div></td>
                    {me.pt && (
                      <td className="psr-nowrap">
                        {l.status !== 'approved' && <button type="button" className="sp-btn primary" onClick={() => decide(l, 'approve')} data-psr-llapprove={l.id}><Check size={13} /> {t('xPsr.approve')}</button>}
                        {l.status !== 'rejected' && <button type="button" className="sp-btn psr-danger" onClick={() => decide(l, 'reject')} data-psr-llreject={l.id}>{t('xPsr.reject')}</button>}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="psr-dlg-foot"><button type="button" className="sp-btn" onClick={onClose}>{t('xPsr.close')}</button></div>
      </div>
      {asking && <LineRequestDialog me={me} vendors={vendors} vendor={me.isVendor ? null : vendor} onClose={() => setAsking(false)} onDone={() => { setAsking(false); load(); onChanged(); }} />}
    </div>, document.body,
  );
}

/* ---------- the Sewing plan tab ---------- */
const SP_ST = ['late', 'prog', 'planned', 'done', 'none'];
const lineStatus = (x, today) => (x.actEnd ? 'done' : x.actStart ? (x.planEnd && x.planEnd < today ? 'late' : 'prog')
  : x.planStart && x.planStart < today ? 'late' : x.planStart || x.planEnd ? 'planned' : 'none');
const dueOf = (l) => (l.cfmByVendor && l.cfmExf ? l.cfmExf : l.reqExf);

function MatCell({ m }) {
  const { t } = useTranslation();
  if (m?.ready) return <>{dm(m.ready)}</>;
  if (m?.plan) return <span className="psr-muted">{t('xPsr.matPlan', { d: dm(m.plan) })}</span>;
  return <span className="psr-muted">—</span>;
}

function ByLine({ lots, me, today, onOpen }) {
  const { t } = useTranslation();
  const [quick, setQuick] = useState('');
  const all = useMemo(() => {
    const out = [];
    lots.forEach((l) => {
      const due = dueOf(l);
      if (!(l.sewing || []).length) { if (l.state === 'open' && l.status !== 'shipped') out.push({ key: `${l.id}-none`, lot: l, x: null, st: 'none', due }); return; }
      l.sewing.forEach((x) => out.push({ key: `${l.id}-${x.id}`, lot: l, x, st: lineStatus(x, today), due, afterExf: !!(x.planEnd && due && x.planEnd > due) }));
    });
    return out;
  }, [lots, today]);
  const rows = useMemo(() => all.filter((r) => !quick || (quick === 'after' ? r.afterExf : r.st === quick)), [all, quick]);
  const columns = useMemo(() => [
    { key: 'st', get: (r) => t(`xPsr.sp_${r.st}`) },
    { key: 'vendor', get: (r) => r.lot.vendor },
    { key: 'line', get: (r) => (r.x ? lineText(t, r.x) || t('xPsr.noLineNo') : '') },
    { key: 'part', get: (r) => (r.x ? t(`xPsr.part_${r.x.part}`) : '') },
    { key: 'po', get: (r) => `${r.lot.vendorOrderNo || ''} ${(r.lot.pos || []).join(', ')}`.trim() },
    { key: 'item', get: (r) => `${r.lot.description || ''} · ${r.lot.color || ''}` },
    { key: 'qty', get: (r) => Number(r.lot.reqQty) },
    { key: 'mat', get: (r) => r.lot.material?.ready || '' },
    { key: 'ps', get: (r) => r.x?.planStart || '' },
    { key: 'pe', get: (r) => r.x?.planEnd || '' },
    { key: 'as', get: (r) => r.x?.actStart || '' },
    { key: 'ae', get: (r) => r.x?.actEnd || '' },
    { key: 'out', get: (r) => (r.x ? Number(r.x.output || 0) : '') },
    { key: 'due', get: (r) => r.due || '' },
    { key: 'rsn', get: (r) => (r.x?.lateReason ? t(`xPsr.lr_${r.x.lateReason}`) : r.x && lateStart(r.x, today) ? t('xPsr.missing') : '') },
  ], [t, today]);
  const tools = useColumnTools(rows, columns);
  const cnt = (k) => all.filter((r) => r.st === k).length;
  const after = all.filter((r) => r.afterExf).length;
  const lateD = (d) => d && d < today;
  return (
    <>
      <div className="psr-bar">
        <div className="psr-pills" role="group">
          <button type="button" className={!quick ? 'on' : ''} onClick={() => setQuick('')}>{t('xPsr.all')} <em>{all.length}</em></button>
          {SP_ST.map((k) => <button key={k} type="button" className={quick === k ? 'on' : ''} onClick={() => setQuick(k)} data-psr-sppill={k}>{t(`xPsr.sp_${k}`)} <em>{cnt(k)}</em></button>)}
          <button type="button" className={quick === 'after' ? 'on' : ''} onClick={() => setQuick('after')} data-psr-sppill="after">{t('xPsr.spAfter')} <em>{after}</em></button>
        </div>
        {tools.active && <button type="button" className="sp-btn" onClick={tools.clear}>{t('xPsr.clearFilters')}</button>}
        <span className="psr-shown">{t('xPsr.spLinesOf', { n: tools.shown.length, m: all.length })}</span>
      </div>
      <p className="psr-muted psr-small">{t('xPsr.spHelpLine')}</p>
      <div className="psr-scroll">
        <table className="sp-table psr-table psr-sptable" data-psr-spline>
          <thead><tr>
            <SortTh label={t('xPsr.colStatus')} {...tools.thProps('st')} />
            {!me.isVendor && <SortTh label={t('xPsr.colVendor')} {...tools.thProps('vendor')} />}
            <SortTh label={t('xPsr.sew_lineId')} {...tools.thProps('line')} />
            <SortTh label={t('xPsr.sew_part')} {...tools.thProps('part')} />
            <SortTh label={t('xPsr.colLot')} {...tools.thProps('po')} />
            <SortTh label={t('xPsr.colItem')} {...tools.thProps('item')} />
            <SortTh label={t('xPsr.colQty')} className="num" {...tools.thProps('qty')} />
            <SortTh label={t('xPsr.colMat')} {...tools.thProps('mat')} />
            <SortTh label={t('xPsr.sew_planStart')} {...tools.thProps('ps')} />
            <SortTh label={t('xPsr.sew_planEnd')} {...tools.thProps('pe')} />
            <SortTh label={t('xPsr.sew_actStart')} {...tools.thProps('as')} />
            <SortTh label={t('xPsr.sew_actEnd')} {...tools.thProps('ae')} />
            <SortTh label={t('xPsr.colOutput')} className="num" {...tools.thProps('out')} />
            <SortTh label={t('xPsr.colExf')} {...tools.thProps('due')} />
            <SortTh label={t('xPsr.sew_lateReason')} {...tools.thProps('rsn')} />
          </tr></thead>
          <tbody>
            {!tools.shown.length && <tr><td colSpan={15} className="psr-muted">{t('xPsr.noRows')}</td></tr>}
            {tools.shown.map((r) => {
              const x = r.x || {};
              return (
                <tr key={r.key} className="psr-click" onClick={() => onOpen(r.lot.id)} data-psr-sprow={r.key}>
                  <td><span className={`psr-sp ${r.st}`}>{t(`xPsr.sp_${r.st}`)}</span></td>
                  {!me.isVendor && <td>{r.lot.vendor}</td>}
                  <td className="psr-nowrap">{r.x ? (r.x.lineName ? <LineName entry={{ kind: x.lineKind, name: x.lineName, status: x.lineStatus }} /> : <span className="psr-muted">{t('xPsr.noLineNo')}</span>) : <span className="psr-muted">—</span>}</td>
                  <td>{r.x ? t(`xPsr.part_${x.part}`) : <span className="psr-muted">—</span>}</td>
                  <td className="psr-nowrap"><b>{r.lot.vendorOrderNo || '—'}</b><div className="psr-muted psr-small">{(r.lot.pos || []).join(', ')}</div></td>
                  <td className="psr-item"><b>{r.lot.description}</b> <span className="psr-muted">{r.lot.color}</span></td>
                  <td className="num">{num(r.lot.reqQty)}</td>
                  <td className="psr-nowrap"><MatCell m={r.lot.material} /></td>
                  <td className={`psr-nowrap ${!x.actStart && lateD(x.planStart) ? 'psr-late' : ''}`}>{x.planStart ? dm(x.planStart) : '—'}</td>
                  <td className={`psr-nowrap ${(!x.actEnd && lateD(x.planEnd)) || r.afterExf ? 'psr-late' : ''}`}>{x.planEnd ? dm(x.planEnd) : '—'}{r.afterExf && <span className="psr-late" title={t('xPsr.spAfter')}> {t('xPsr.afterExfMark')}</span>}</td>
                  <td className="psr-nowrap">{x.actStart ? dm(x.actStart) : '—'}</td>
                  <td className="psr-nowrap">{x.actEnd ? dm(x.actEnd) : '—'}</td>
                  <td className="num">{r.x && x.output ? num(x.output) : <span className="psr-muted">—</span>}</td>
                  <td className="psr-nowrap">{dm(r.due)}</td>
                  <td>{x.lateReason ? <>{t(`xPsr.lr_${x.lateReason}`)}{x.lateNote ? <span className="psr-muted"> · {x.lateNote}</span> : null}</> : r.x && lateStart(x, today) ? <span className="psr-late">{t('xPsr.missing')}</span> : <span className="psr-muted">—</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ByOrder({ lots, me, today, onOpen }) {
  const { t } = useTranslation();
  const [quick, setQuick] = useState('');
  const all = useMemo(() => lots.filter((l) => l.state !== 'cancelled').map((l) => {
    const L = l.sewing || [];
    const part = (xs) => {
      const ps = xs.map((x) => x.planStart).filter(Boolean).sort(); const as = xs.map((x) => x.actStart).filter(Boolean).sort();
      return { n: xs.length, ps: ps[0] || null, as: as[0] || null, out: xs.length ? xs.reduce((a, x) => a + (x.output || 0), 0) : null, late: xs.some((x) => lateStart(x, today)),
        miss: xs.some((x) => lateStart(x, today) && !x.lateReason), rs: [...new Set(xs.map((x) => x.lateReason).filter(Boolean))], notes: xs.map((x) => x.lateNote).filter(Boolean) };
    };
    const sk = part(l.fullOnly ? L.filter((x) => x.part === 'full') : L.filter((x) => x.part !== 'case'));
    const cs = l.fullOnly ? null : part(L.filter((x) => x.part === 'case'));
    return { key: l.id, lot: l, due: dueOf(l), sk, cs, late: sk.late || !!cs?.late, miss: sk.miss || !!cs?.miss, rs: [...new Set([...sk.rs, ...(cs ? cs.rs : [])])],
      notes: [...sk.notes, ...(cs ? cs.notes : [])], noPlan: !L.length };
  }), [lots, today]);
  const rows = useMemo(() => all.filter((r) => !quick || (quick === 'late' ? r.late : quick === 'miss' ? r.miss : r.noPlan && r.lot.status !== 'shipped')), [all, quick]);
  const columns = useMemo(() => [
    { key: 'vendor', get: (r) => r.lot.vendor },
    { key: 'po', get: (r) => `${r.lot.vendorOrderNo || ''} ${(r.lot.pos || []).join(', ')}`.trim() },
    { key: 'item', get: (r) => r.lot.description || '' },
    { key: 'qty', get: (r) => Number(r.lot.reqQty) },
    { key: 'color', get: (r) => r.lot.color || '' },
    { key: 'due', get: (r) => r.due || '' },
    { key: 'mat', get: (r) => r.lot.material?.ready || '' },
    { key: 'sps', get: (r) => r.sk.ps || '' },
    { key: 'cps', get: (r) => r.cs?.ps || '' },
    { key: 'sas', get: (r) => r.sk.as || '' },
    { key: 'cas', get: (r) => r.cs?.as || '' },
    { key: 'sout', get: (r) => (r.sk.out == null ? '' : r.sk.out) },
    { key: 'cout', get: (r) => (r.cs?.out == null ? '' : r.cs.out) },
    { key: 'rsn', get: (r) => (r.rs.length ? r.rs.map((k) => t(`xPsr.lr_${k}`)).join(', ') : r.miss ? t('xPsr.missing') : '') },
  ], [t]);
  const tools = useColumnTools(rows, columns);
  const dt = (d, late, n) => (d ? <><span className={late ? 'psr-late' : ''}>{dm(d)}</span>{n > 1 ? <span className="psr-muted"> · {t('xPsr.nLines', { n })}</span> : null}</> : <span className="psr-muted">—</span>);
  const act = (p) => (!p ? <span className="psr-muted">{t('xPsr.na')}</span> : <>{dt(p.as, p.late, 0)}{!p.as && p.late ? <span className="psr-late"> {t('xPsr.notStarted')}</span> : null}</>);
  return (
    <>
      <div className="psr-bar">
        <div className="psr-pills" role="group">
          <button type="button" className={!quick ? 'on' : ''} onClick={() => setQuick('')}>{t('xPsr.all')} <em>{all.length}</em></button>
          <button type="button" className={quick === 'late' ? 'on' : ''} onClick={() => setQuick('late')} data-psr-sopill="late">{t('xPsr.spLateStart')} <em>{all.filter((r) => r.late).length}</em></button>
          <button type="button" className={quick === 'miss' ? 'on' : ''} onClick={() => setQuick('miss')} data-psr-sopill="miss">{t('xPsr.spMissing')} <em>{all.filter((r) => r.miss).length}</em></button>
          <button type="button" className={quick === 'noplan' ? 'on' : ''} onClick={() => setQuick('noplan')} data-psr-sopill="noplan">{t('xPsr.sp_none')} <em>{all.filter((r) => r.noPlan && r.lot.status !== 'shipped').length}</em></button>
        </div>
        {tools.active && <button type="button" className="sp-btn" onClick={tools.clear}>{t('xPsr.clearFilters')}</button>}
        <span className="psr-shown">{t('xPsr.spOrdersOf', { n: tools.shown.length, m: all.length })}</span>
      </div>
      <p className="psr-muted psr-small">{t('xPsr.spHelpOrder')}</p>
      <div className="psr-scroll">
        <table className="sp-table psr-table psr-sptable" data-psr-sporder>
          <thead><tr>
            {!me.isVendor && <SortTh label={t('xPsr.colVendor')} {...tools.thProps('vendor')} />}
            <SortTh label={t('xPsr.colLot')} {...tools.thProps('po')} />
            <SortTh label={t('xPsr.colStyle')} {...tools.thProps('item')} />
            <SortTh label={t('xPsr.colQty')} className="num" {...tools.thProps('qty')} />
            <SortTh label={t('xPsr.colColor')} {...tools.thProps('color')} />
            <SortTh label={t('xPsr.colExf')} {...tools.thProps('due')} />
            <SortTh label={t('xPsr.colMat')} {...tools.thProps('mat')} />
            <SortTh label={t('xPsr.colEstSkin')} {...tools.thProps('sps')} />
            <SortTh label={t('xPsr.colEstCase')} {...tools.thProps('cps')} />
            <SortTh label={t('xPsr.colActSkin')} {...tools.thProps('sas')} />
            <SortTh label={t('xPsr.colActCase')} {...tools.thProps('cas')} />
            <SortTh label={t('xPsr.colOutSkin')} className="num" {...tools.thProps('sout')} />
            <SortTh label={t('xPsr.colOutCase')} className="num" {...tools.thProps('cout')} />
            <SortTh label={t('xPsr.sew_lateReason')} {...tools.thProps('rsn')} />
          </tr></thead>
          <tbody>
            {!tools.shown.length && <tr><td colSpan={14} className="psr-muted">{t('xPsr.noRows')}</td></tr>}
            {tools.shown.map((r) => (
              <tr key={r.key} className="psr-click" onClick={() => onOpen(r.lot.id)} data-psr-sorow={r.key}>
                {!me.isVendor && <td>{r.lot.vendor}</td>}
                <td className="psr-nowrap"><b>{r.lot.vendorOrderNo || '—'}</b><div className="psr-muted psr-small">{(r.lot.pos || []).join(', ')}</div></td>
                <td className="psr-item"><b>{r.lot.description}</b>{r.lot.fullOnly && <span className="psr-tag"> {t('xPsr.part_full')}</span>}</td>
                <td className="num">{num(r.lot.reqQty)}</td>
                <td>{r.lot.color}</td>
                <td className="psr-nowrap">{dm(r.due)}</td>
                <td className="psr-nowrap"><MatCell m={r.lot.material} /></td>
                <td className="psr-nowrap">{dt(r.sk.ps, false, r.sk.n)}</td>
                <td className="psr-nowrap">{r.cs ? dt(r.cs.ps, false, r.cs.n) : <span className="psr-muted">{t('xPsr.na')}</span>}</td>
                <td className="psr-nowrap">{act(r.sk)}</td>
                <td className="psr-nowrap">{act(r.cs)}</td>
                <td className="num">{r.sk.out != null ? num(r.sk.out) : <span className="psr-muted">—</span>}</td>
                <td className="num">{!r.cs ? <span className="psr-muted">{t('xPsr.na')}</span> : r.cs.out != null ? num(r.cs.out) : <span className="psr-muted">—</span>}</td>
                <td>{r.rs.length ? <>{r.rs.map((k) => t(`xPsr.lr_${k}`)).join(', ')}{r.notes.length ? <span className="psr-muted"> · {r.notes.join('; ')}</span> : null}</>
                  : r.miss ? <span className="psr-late">{t('xPsr.missing')}</span> : <span className="psr-muted">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function SewingPlan({ lots, me, today, onOpen, onLineList, linesPending }) {
  const { t } = useTranslation();
  const [by, setBy] = useState(() => { try { return localStorage.getItem('psr.sew.by') === 'order' ? 'order' : 'line'; } catch { return 'line'; } });
  const pick = (v) => { setBy(v); try { localStorage.setItem('psr.sew.by', v); } catch { /* a private window forgets */ } };
  return (
    <div className="psr-sewplan" data-psr-sewplan>
      <div className="psr-bar">
        <div className="psr-pills" role="group" aria-label={t('xPsr.tabSewing')}>
          <button type="button" className={by === 'line' ? 'on' : ''} onClick={() => pick('line')} data-psr-spby="line">{t('xPsr.spByLine')}</button>
          <button type="button" className={by === 'order' ? 'on' : ''} onClick={() => pick('order')} data-psr-spby="order">{t('xPsr.spByOrder')}</button>
        </div>
        <span className="mh-grow" />
        <button type="button" className="sp-btn" onClick={onLineList} data-psr-linelist>{t('xPsr.spLineList')}{linesPending ? <em className="psr-count">{linesPending}</em> : null}</button>
      </div>
      {by === 'line' ? <ByLine lots={lots} me={me} today={today} onOpen={onOpen} /> : <ByOrder lots={lots} me={me} today={today} onOpen={onOpen} />}
    </div>
  );
}
